import { useState, useEffect, useCallback, useRef } from "react";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import axios from "axios";
import { toast } from "sonner";
import { 
  Plus, Trash, FilePdf, BookOpen, MagnifyingGlass, Calendar, Package, X, Check, PencilSimple, CaretUp, CaretDown, CloudArrowUp, Image as ImageIcon, FileXls
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { getApiErrorMessage } from "@/lib/apiError";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import OptimizedImage from "@/components/OptimizedImage";
import { useSettings } from "@/contexts/SettingsContext";
import { PageHeader } from "@/components/PageHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PhysicalCatalogs() {
  const { isReadOnly } = useSettings();
  const [catalogs, setCatalogs] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientsWithDeliveries, setClientsWithDeliveries] = useState([]);
  const [gabinetesWithDeliveries, setGabinetesWithDeliveries] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddCatalog, setShowAddCatalog] = useState(false);
  const [showEditCatalog, setShowEditCatalog] = useState(false);
  const [editingCatalog, setEditingCatalog] = useState(null);
  const [showAddDelivery, setShowAddDelivery] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCatalog, setFilterCatalog] = useState("all");
  const [activeTab, setActiveTab] = useState("clients"); // "clients" or "gabinetes"
  
  // Form states
  const [newCatalogName, setNewCatalogName] = useState("");
  const [newCatalogYear, setNewCatalogYear] = useState(new Date().getFullYear());
  const [newCatalogImage, setNewCatalogImage] = useState(null);
  const [newCatalogImagePreview, setNewCatalogImagePreview] = useState(null);
  const [uploadingCatalogImage, setUploadingCatalogImage] = useState(false);
  const catalogImageInputRef = useRef(null);
  const editCatalogImageInputRef = useRef(null);
  const [deliveryForm, setDeliveryForm] = useState({
    catalog_id: "",
    client_id: "",
    quantity: 1,
    delivery_date: new Date().toISOString().split('T')[0],
    notes: ""
  });
  const [editingDeliveryId, setEditingDeliveryId] = useState(null);
  const [clientSearch, setClientSearch] = useState("");
  const [sortBy, setSortBy] = useState("date"); // "date", "quantity", "name"
  const [sortOrder, setSortOrder] = useState("desc"); // "asc", "desc"
  const [expandedClientId, setExpandedClientId] = useState(null); // For inline delivery editing
  const [expandedGabineteId, setExpandedGabineteId] = useState(null); // For inline gabinete delivery editing
  const [gabineteDeliveries, setGabineteDeliveries] = useState([]); // Deliveries for expanded gabinete
  const [editingGabineteDeliveryId, setEditingGabineteDeliveryId] = useState(null);
  const [gabineteDeliveryForm, setGabineteDeliveryForm] = useState({ quantity: 1, delivery_date: "" });
  const [deliveryCatalogFilter, setDeliveryCatalogFilter] = useState("all"); // Filter deliveries by catalog

  const fetchData = useCallback(async () => {
    try {
      const [catalogsRes, deliveriesRes, clientsRes, summaryRes, clientsDeliveriesRes, gabinetesDeliveriesRes] = await Promise.all([
        axios.get(`${API}/physical-catalogs`),
        axios.get(`${API}/catalog-deliveries`),
        axios.get(`${API}/clients`),
        axios.get(`${API}/catalog-deliveries/summary`),
        axios.get(`${API}/catalog-deliveries/by-client`),
        axios.get(`${API}/catalog-deliveries/by-gabinete`)
      ]);
      setCatalogs(catalogsRes.data);
      setDeliveries(deliveriesRes.data);
      setClients(clientsRes.data);
      setSummary(summaryRes.data);
      setClientsWithDeliveries(clientsDeliveriesRes.data);
      setGabinetesWithDeliveries(gabinetesDeliveriesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCatalogImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setNewCatalogImage(file);
      const reader = new FileReader();
      reader.onload = (e) => setNewCatalogImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleAddCatalog = async () => {
    if (!newCatalogName.trim()) {
      toast.error("Insira o nome do catálogo");
      return;
    }
    try {
      setUploadingCatalogImage(true);
      
      // First create the catalog
      const response = await axios.post(`${API}/physical-catalogs`, {
        name: newCatalogName,
        year: newCatalogYear
      });
      
      const catalogId = response.data.id;
      
      // If there's an image, upload it
      if (newCatalogImage && catalogId) {
        const formData = new FormData();
        formData.append('file', newCatalogImage);
        await axios.post(`${API}/physical-catalogs/${catalogId}/image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      toast.success("Catálogo adicionado");
      setNewCatalogName("");
      setNewCatalogImage(null);
      setNewCatalogImagePreview(null);
      setShowAddCatalog(false);
      fetchData();
    } catch (error) {
      toast.error("Erro ao adicionar catálogo");
    } finally {
      setUploadingCatalogImage(false);
    }
  };

  // Edit catalog functions
  const openEditCatalog = (catalog) => {
    setEditingCatalog(catalog);
    setNewCatalogName(catalog.name);
    setNewCatalogYear(catalog.year || new Date().getFullYear());
    setNewCatalogImagePreview(catalog.image_url || null);
    setNewCatalogImage(null);
    setShowEditCatalog(true);
  };

  const handleEditCatalogImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setNewCatalogImage(file);
      const reader = new FileReader();
      reader.onload = (e) => setNewCatalogImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSaveEditCatalog = async () => {
    if (!newCatalogName.trim()) {
      toast.error("Insira o nome do catálogo");
      return;
    }
    try {
      setUploadingCatalogImage(true);
      
      // Update catalog info
      await axios.put(`${API}/physical-catalogs/${editingCatalog.id}`, {
        name: newCatalogName,
        year: newCatalogYear
      });
      
      // If there's a new image, upload it
      if (newCatalogImage) {
        const formData = new FormData();
        formData.append('file', newCatalogImage);
        await axios.post(`${API}/physical-catalogs/${editingCatalog.id}/image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      toast.success("Catálogo atualizado");
      setShowEditCatalog(false);
      setEditingCatalog(null);
      setNewCatalogName("");
      setNewCatalogImage(null);
      setNewCatalogImagePreview(null);
      fetchData();
    } catch (error) {
      toast.error("Erro ao atualizar catálogo");
    } finally {
      setUploadingCatalogImage(false);
    }
  };

  const handleDeleteCatalog = async (catalogId) => {
    if (!window.confirm("Tem a certeza que deseja eliminar este catálogo?")) return;
    try {
      await axios.delete(`${API}/physical-catalogs/${catalogId}`);
      toast.success("Catálogo eliminado");
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erro ao eliminar catálogo"));
    }
  };

  const handleAddDelivery = async () => {
    if (!deliveryForm.catalog_id || !deliveryForm.client_id) {
      toast.error("Selecione o catálogo e o cliente");
      return;
    }
    try {
      if (editingDeliveryId) {
        await axios.put(`${API}/catalog-deliveries/${editingDeliveryId}`, deliveryForm);
        toast.success("Entrega atualizada");
      } else {
        await axios.post(`${API}/catalog-deliveries`, deliveryForm);
        toast.success("Entrega registada");
      }
      setDeliveryForm({
        catalog_id: "",
        client_id: "",
        quantity: 1,
        delivery_date: new Date().toISOString().split('T')[0],
        notes: ""
      });
      setEditingDeliveryId(null);
      setShowAddDelivery(false);
      fetchData();
    } catch (error) {
      toast.error(editingDeliveryId ? "Erro ao atualizar entrega" : "Erro ao registar entrega");
    }
  };

  const handleEditDelivery = (delivery) => {
    setEditingDeliveryId(delivery.id);
    setDeliveryForm({
      catalog_id: delivery.catalog_id,
      client_id: delivery.client_id,
      quantity: delivery.quantity,
      delivery_date: delivery.delivery_date?.split('T')[0] || new Date().toISOString().split('T')[0],
      notes: delivery.notes || ""
    });
  };

  const handleSaveEditDelivery = async () => {
    try {
      await axios.put(`${API}/catalog-deliveries/${editingDeliveryId}`, {
        quantity: deliveryForm.quantity,
        delivery_date: deliveryForm.delivery_date
      });
      toast.success("Entrega atualizada");
      setEditingDeliveryId(null);
      fetchData();
    } catch (error) {
      toast.error("Erro ao atualizar entrega");
    }
  };

  const handleDeleteDelivery = async (deliveryId) => {
    if (!window.confirm("Tem a certeza que deseja eliminar esta entrega?")) return;
    try {
      await axios.delete(`${API}/catalog-deliveries/${deliveryId}`);
      toast.success("Entrega eliminada");
      fetchData();
    } catch (error) {
      toast.error("Erro ao eliminar entrega");
    }
  };

  // Gabinete delivery handlers
  const handleExpandGabinete = async (gabineteId) => {
    if (expandedGabineteId === gabineteId) {
      setExpandedGabineteId(null);
      setGabineteDeliveries([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/gabinetes/${gabineteId}/catalog-deliveries`);
      // Apply catalog filter
      const filteredDeliveries = deliveryCatalogFilter === "all" 
        ? response.data 
        : response.data.filter(d => d.catalog_id === deliveryCatalogFilter);
      setGabineteDeliveries(filteredDeliveries);
      setExpandedGabineteId(gabineteId);
    } catch (error) {
      toast.error("Erro ao carregar entregas");
    }
  };

  const handleEditGabineteDelivery = (delivery) => {
    setEditingGabineteDeliveryId(delivery.id);
    setGabineteDeliveryForm({
      quantity: delivery.quantity,
      delivery_date: delivery.delivery_date?.split('T')[0] || ""
    });
  };

  const handleSaveGabineteDelivery = async () => {
    try {
      await axios.put(`${API}/gabinetes/${expandedGabineteId}/catalog-deliveries/${editingGabineteDeliveryId}`, gabineteDeliveryForm);
      toast.success("Entrega atualizada");
      setEditingGabineteDeliveryId(null);
      // Refresh gabinete deliveries
      const response = await axios.get(`${API}/gabinetes/${expandedGabineteId}/catalog-deliveries`);
      setGabineteDeliveries(response.data);
      fetchData();
    } catch (error) {
      toast.error("Erro ao atualizar entrega");
    }
  };

  const handleDeleteGabineteDelivery = async (deliveryId) => {
    if (!window.confirm("Tem a certeza que deseja eliminar esta entrega?")) return;
    try {
      await axios.delete(`${API}/gabinetes/${expandedGabineteId}/catalog-deliveries/${deliveryId}`);
      toast.success("Entrega eliminada");
      // Refresh gabinete deliveries
      const response = await axios.get(`${API}/gabinetes/${expandedGabineteId}/catalog-deliveries`);
      setGabineteDeliveries(response.data);
      fetchData();
    } catch (error) {
      toast.error("Erro ao eliminar entrega");
    }
  };

  const handleGeneratePDF = () => {
    const url = filterCatalog === "all" 
      ? `${API}/catalog-deliveries/report/pdf`
      : `${API}/catalog-deliveries/report/pdf?catalog_id=${filterCatalog}`;
    
    // Create a temporary link and click it to download
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleGenerateExcel = () => {
    const url = filterCatalog === "all" 
      ? `${API}/catalog-deliveries/report/excel`
      : `${API}/catalog-deliveries/report/excel?catalog_id=${filterCatalog}`;
    
    // Create a temporary link and click it to download
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredDeliveries = deliveries
    .filter(d => {
      const matchesSearch = d.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           d.catalog_name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCatalog = filterCatalog === "all" || d.catalog_id === filterCatalog;
      return matchesSearch && matchesCatalog;
    })
    .sort((a, b) => {
      let comparison = 0;
      if (sortBy === "date") {
        comparison = new Date(a.delivery_date) - new Date(b.delivery_date);
      } else if (sortBy === "quantity") {
        comparison = a.quantity - b.quantity;
      } else if (sortBy === "name") {
        comparison = a.client_name.localeCompare(b.client_name);
      }
      return sortOrder === "asc" ? comparison : -comparison;
    });

  const toggleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("desc");
    }
  };

  const totalDelivered = filteredDeliveries.reduce((sum, d) => sum + d.quantity, 0);

  // Combine clients with deliveries and clients without deliveries
  const allClientsWithDeliveryInfo = (() => {
    const clientsWithDeliveriesMap = new Map(
      clientsWithDeliveries.map(c => [c.client_id, c])
    );
    
    // Add clients that have no deliveries at all
    const allClients = [...clientsWithDeliveries];
    clients.forEach(client => {
      if (!clientsWithDeliveriesMap.has(client.id)) {
        allClients.push({
          client_id: client.id,
          client_name: client.name,
          client_city: client.city || "",
          total_quantity: 0,
          delivery_count: 0,
          last_delivery: null
        });
      }
    });
    return allClients;
  })();

  // Calculate total for filtered catalog
  const totalForFilter = deliveryCatalogFilter === "all" 
    ? totalDelivered 
    : deliveries.filter(d => d.catalog_id === deliveryCatalogFilter).reduce((sum, d) => sum + d.quantity, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size={40} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="physical-catalogs">
      {/* Header */}
      <PageHeader
        icon={BookOpen}
        title="Gestão de Catálogos"
        actions={!isReadOnly && (
          <>
            <Button
              variant="outline"
              className="btn-secondary"
              onClick={() => setShowAddCatalog(true)}
              data-testid="btn-add-catalog-type"
            >
              <BookOpen size={18} />
              Novo Tipo
            </Button>
            <Button
              className="btn-primary"
              onClick={() => setShowAddDelivery(true)}
              data-testid="btn-add-delivery"
            >
              <Plus size={18} />
              Registar Entrega
            </Button>
          </>
        )}
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {summary.map((s) => {
          const catalog = catalogs.find(c => c.id === s.catalog_id);
          return (
            <div key={s.catalog_id} className="gresart-card p-4 relative group">
              {/* Edit button */}
              <button
                onClick={() => catalog && openEditCatalog(catalog)}
                className="absolute top-2 right-2 p-1.5 bg-white/80 hover:bg-white rounded-lg shadow-sm opacity-0 group-hover:opacity-100 transition-opacity z-10"
                title="Editar catálogo"
                data-testid={`btn-edit-catalog-${s.catalog_id}`}
              >
                <PencilSimple size={16} className="text-stone-500 hover:text-[#009392]" />
              </button>
              
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-2xl font-bold text-[#009392]">{s.total_delivered}</p>
                  <p className="text-sm text-stone-600 font-medium">{s.catalog_name}</p>
                </div>
                {s.image_url ? (
                  <div className="flex-shrink-0 rounded-lg overflow-hidden border border-stone-200 shadow-sm" style={{ width: '50px', aspectRatio: '1/1.4' }}>
                    <OptimizedImage 
                      src={s.image_url} 
                      alt={s.catalog_name}
                      className="w-full h-full object-cover"
                      width={50}
                    />
                  </div>
                ) : (
                  <div className="p-2 rounded-lg bg-[#009392]/10 flex-shrink-0">
                    <BookOpen size={24} className="text-[#009392]" />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="gresart-card p-4">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-3 flex-1">
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
              <Input
                placeholder="Pesquisar por cliente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-deliveries"
              />
            </div>
            <Select value={filterCatalog} onValueChange={setFilterCatalog}>
              <SelectTrigger className="w-full sm:w-[200px]" data-testid="filter-catalog">
                <SelectValue placeholder="Filtrar por catálogo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os catálogos</SelectItem>
                {catalogs.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button 
            variant="outline" 
            className="btn-secondary"
            onClick={handleGeneratePDF}
            data-testid="btn-generate-pdf"
          >
            <FilePdf size={18} />
            Gerar PDF
          </Button>
          <Button 
            variant="outline" 
            className="btn-secondary"
            onClick={handleGenerateExcel}
            data-testid="btn-generate-excel"
          >
            <FileXls size={18} />
            Exportar Excel
          </Button>
        </div>
      </div>

      {/* Catalog Filter */}
      <div className="flex items-center gap-4 mb-4">
        <label className="text-sm font-medium text-stone-600">Filtrar por catálogo:</label>
        <select
          value={deliveryCatalogFilter}
          onChange={(e) => setDeliveryCatalogFilter(e.target.value)}
          className="px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-[#009392] focus:border-[#009392] outline-none"
          data-testid="filter-catalog"
        >
          <option value="all">Todos os catálogos</option>
          {catalogs.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        {deliveryCatalogFilter !== "all" && (
          <button
            onClick={() => setDeliveryCatalogFilter("all")}
            className="text-sm text-[#009392] hover:underline"
          >
            Limpar filtro
          </button>
        )}
      </div>

      {/* Tabs for Clients / Gabinetes */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setActiveTab("clients")}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === "clients" 
              ? "bg-[#009392] text-white" 
              : "bg-stone-100 text-stone-600 hover:bg-stone-200"
          }`}
        >
          Clientes ({allClientsWithDeliveryInfo.length})
        </button>
        <button
          onClick={() => setActiveTab("gabinetes")}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === "gabinetes" 
              ? "bg-[#009392] text-white" 
              : "bg-stone-100 text-stone-600 hover:bg-stone-200"
          }`}
        >
          Gabinetes ({gabinetesWithDeliveries.filter(g => g.total_quantity > 0).length})
        </button>
      </div>

      {/* Clients Table - Shows ALL clients with expandable delivery details */}
      {activeTab === "clients" && (
      <div className="gresart-card">
        <div className="flex items-center justify-between mb-4 p-4 pb-0">
          <h2 className="text-lg font-semibold font-[Manrope] text-stone-900">
            Entregas por Cliente
          </h2>
          <span className="text-sm text-stone-500">
            Total: <strong className="text-[#009392]">{totalForFilter}</strong> unidades
            {deliveryCatalogFilter !== "all" && (
              <span className="text-xs text-stone-400 ml-1">(catálogo selecionado)</span>
            )}
          </span>
        </div>

        {allClientsWithDeliveryInfo.length === 0 ? (
          <div className="empty-state py-12">
            <Package size={48} className="text-stone-300 mb-4" />
            <h3 className="text-lg font-medium text-stone-900 mb-2">Sem clientes</h3>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="gresart-table">
              <thead>
                <tr>
                  <th className="w-10"></th>
                  <th>Nº</th>
                  <th 
                    className="cursor-pointer hover:bg-stone-50 select-none"
                    onClick={() => toggleSort("name")}
                  >
                    <div className="flex items-center gap-1">
                      Cliente
                      {sortBy === "name" && (
                        sortOrder === "asc" ? <CaretUp size={14} /> : <CaretDown size={14} />
                      )}
                    </div>
                  </th>
                  <th>Localidade</th>
                  <th 
                    className="text-center cursor-pointer hover:bg-stone-50 select-none"
                    onClick={() => toggleSort("quantity")}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Total
                      {sortBy === "quantity" && (
                        sortOrder === "asc" ? <CaretUp size={14} /> : <CaretDown size={14} />
                      )}
                    </div>
                  </th>
                  <th className="text-center">Entregas</th>
                  <th 
                    className="text-center cursor-pointer hover:bg-stone-50 select-none"
                    onClick={() => toggleSort("date")}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Última
                      {sortBy === "date" && (
                        sortOrder === "asc" ? <CaretUp size={14} /> : <CaretDown size={14} />
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {allClientsWithDeliveryInfo
                  .filter(c => {
                    // Filter by search term
                    const matchesSearch = c.client_name.toLowerCase().includes(searchTerm.toLowerCase());
                    return matchesSearch;
                  })
                  .sort((a, b) => {
                    let comparison = 0;
                    if (sortBy === "date") {
                      const dateA = a.last_delivery ? new Date(a.last_delivery) : new Date(0);
                      const dateB = b.last_delivery ? new Date(b.last_delivery) : new Date(0);
                      comparison = dateA - dateB;
                    } else if (sortBy === "quantity") {
                      // Get quantity for filtered catalog or total
                      const qtyA = deliveryCatalogFilter === "all" 
                        ? a.total_quantity 
                        : deliveries.filter(d => d.client_id === a.client_id && d.catalog_id === deliveryCatalogFilter).reduce((sum, d) => sum + d.quantity, 0);
                      const qtyB = deliveryCatalogFilter === "all" 
                        ? b.total_quantity 
                        : deliveries.filter(d => d.client_id === b.client_id && d.catalog_id === deliveryCatalogFilter).reduce((sum, d) => sum + d.quantity, 0);
                      comparison = qtyA - qtyB;
                    } else if (sortBy === "name") {
                      comparison = a.client_name.localeCompare(b.client_name);
                    }
                    return sortOrder === "asc" ? comparison : -comparison;
                  })
                  .map((client, index) => {
                    const clientDeliveries = deliveries
                      .filter(d => d.client_id === client.client_id)
                      .filter(d => deliveryCatalogFilter === "all" || d.catalog_id === deliveryCatalogFilter);
                    const isExpanded = expandedClientId === client.client_id;
                    // Calculate quantity for filtered view
                    const displayQuantity = deliveryCatalogFilter === "all" 
                      ? client.total_quantity 
                      : clientDeliveries.reduce((sum, d) => sum + d.quantity, 0);
                    
                    return (
                      <>
                        <tr 
                          key={client.client_id} 
                          className={`${displayQuantity === 0 ? "bg-stone-50/50 text-stone-400" : ""} ${clientDeliveries.length > 0 ? "cursor-pointer hover:bg-stone-50" : ""}`}
                          onClick={() => clientDeliveries.length > 0 && setExpandedClientId(isExpanded ? null : client.client_id)}
                        >
                          <td className="text-center">
                            {clientDeliveries.length > 0 && (
                              isExpanded ? <CaretUp size={16} className="text-[#009392]" /> : <CaretDown size={16} className="text-stone-400" />
                            )}
                          </td>
                          <td className="text-stone-400">{index + 1}</td>
                          <td className={`font-medium ${displayQuantity > 0 ? 'text-stone-900' : 'text-stone-400'}`}>{client.client_name}</td>
                          <td className="text-stone-500">{client.client_city || "-"}</td>
                          <td className="text-center">
                            <span className={`font-bold ${displayQuantity > 0 ? 'text-[#009392]' : 'text-stone-300'}`}>
                              {displayQuantity}
                            </span>
                          </td>
                          <td className="text-center text-stone-500">
                            {deliveryCatalogFilter === "all" ? client.delivery_count : clientDeliveries.length}
                          </td>
                          <td className="text-center text-stone-500">
                            {client.last_delivery 
                              ? new Date(client.last_delivery).toLocaleDateString('pt-PT')
                              : "-"
                            }
                          </td>
                        </tr>
                        {/* Expanded Deliveries Row */}
                        {isExpanded && clientDeliveries.length > 0 && (
                          <tr key={`${client.client_id}-deliveries`} className="bg-stone-50/50">
                            <td colSpan={7} className="p-0">
                              <div className="px-6 py-3 border-l-4 border-[#009392]">
                                <div className="space-y-2">
                                  {clientDeliveries.map((delivery) => (
                                    <div 
                                      key={delivery.id} 
                                      className="flex items-center justify-between bg-white rounded-lg p-3 border border-stone-200"
                                    >
                                      <div className="flex items-center gap-4">
                                        <span className="px-2 py-1 bg-[#009392]/10 text-[#009392] text-xs font-medium rounded">
                                          {delivery.catalog_name}
                                        </span>
                                        {editingDeliveryId === delivery.id ? (
                                          <>
                                            <div className="flex items-center gap-2">
                                              <span className="text-xs text-stone-500">Qtd:</span>
                                              <Input
                                                type="number"
                                                min="1"
                                                value={deliveryForm.quantity}
                                                onChange={(e) => setDeliveryForm(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                                                className="w-16 h-8 text-sm text-center"
                                                onClick={(e) => e.stopPropagation()}
                                              />
                                            </div>
                                            <div className="flex items-center gap-2">
                                              <span className="text-xs text-stone-500">Data:</span>
                                              <Input
                                                type="date"
                                                value={deliveryForm.delivery_date}
                                                onChange={(e) => setDeliveryForm(prev => ({ ...prev, delivery_date: e.target.value }))}
                                                className="w-32 h-8 text-sm"
                                                onClick={(e) => e.stopPropagation()}
                                              />
                                            </div>
                                          </>
                                        ) : (
                                          <>
                                            <span className="text-sm">
                                              <strong className="text-[#009392]">{delivery.quantity}</strong> unidades
                                            </span>
                                            <span className="text-sm text-stone-500">
                                              {new Date(delivery.delivery_date).toLocaleDateString('pt-PT')}
                                            </span>
                                          </>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-1">
                                        {editingDeliveryId === delivery.id ? (
                                          <>
                                            <button
                                              onClick={(e) => { e.stopPropagation(); handleSaveEditDelivery(); }}
                                              className="p-1.5 hover:bg-green-100 text-green-600 rounded transition-colors"
                                              data-testid={`btn-save-delivery-${delivery.id}`}
                                            >
                                              <Check size={16} />
                                            </button>
                                            <button
                                              onClick={(e) => { e.stopPropagation(); setEditingDeliveryId(null); }}
                                              className="p-1.5 hover:bg-stone-100 text-stone-500 rounded transition-colors"
                                            >
                                              <X size={16} />
                                            </button>
                                          </>
                                        ) : (
                                          <>
                                            <button
                                              onClick={(e) => { e.stopPropagation(); handleEditDelivery(delivery); }}
                                              className="p-1.5 hover:bg-stone-100 text-stone-500 rounded transition-colors"
                                              data-testid={`btn-edit-delivery-${delivery.id}`}
                                            >
                                              <PencilSimple size={16} />
                                            </button>
                                            <button
                                              onClick={(e) => { e.stopPropagation(); handleDeleteDelivery(delivery.id); }}
                                              className="p-1.5 hover:bg-red-100 text-stone-400 hover:text-red-500 rounded transition-colors"
                                              data-testid={`btn-delete-delivery-${delivery.id}`}
                                            >
                                              <Trash size={16} />
                                            </button>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {/* Gabinetes Table */}
      {activeTab === "gabinetes" && (
      <div className="gresart-card">
        <div className="flex items-center justify-between mb-4 p-4 pb-0">
          <h2 className="text-lg font-semibold font-[Manrope] text-stone-900">
            Entregas por Gabinete
          </h2>
          <span className="text-sm text-stone-500">
            Total: <strong className="text-[#009392]">{gabinetesWithDeliveries.reduce((sum, g) => sum + g.total_quantity, 0)}</strong> unidades
          </span>
        </div>

        {gabinetesWithDeliveries.length === 0 ? (
          <div className="empty-state py-12">
            <Package size={48} className="text-stone-300 mb-4" />
            <h3 className="text-lg font-medium text-stone-900 mb-2">Sem gabinetes</h3>
            <p className="text-stone-500">Adicione gabinetes na secção Promoção</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="gresart-table">
              <thead>
                <tr>
                  <th className="w-10"></th>
                  <th>Nº</th>
                  <th>Gabinete</th>
                  <th>Tipo</th>
                  <th className="text-center">Total</th>
                  <th className="text-center">Entregas</th>
                  <th className="text-center">Última</th>
                </tr>
              </thead>
              <tbody>
                {gabinetesWithDeliveries
                  .filter(g => g.gabinete_name.toLowerCase().includes(searchTerm.toLowerCase()))
                  .sort((a, b) => a.gabinete_name.localeCompare(b.gabinete_name))
                  .map((gabinete, index) => {
                    const isExpanded = expandedGabineteId === gabinete.gabinete_id;
                    
                    return (
                      <>
                        <tr 
                          key={gabinete.gabinete_id} 
                          className={`${gabinete.total_quantity === 0 ? "opacity-50" : ""} ${gabinete.delivery_count > 0 ? "cursor-pointer hover:bg-stone-50" : ""}`}
                          onClick={() => gabinete.delivery_count > 0 && handleExpandGabinete(gabinete.gabinete_id)}
                        >
                          <td className="text-center">
                            {gabinete.delivery_count > 0 && (
                              isExpanded ? <CaretUp size={16} className="text-[#009392]" /> : <CaretDown size={16} className="text-stone-400" />
                            )}
                          </td>
                          <td className="text-stone-400">{index + 1}</td>
                          <td className="font-medium text-stone-900">{gabinete.gabinete_name}</td>
                          <td>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium text-white ${
                              gabinete.gabinete_type === "ARQUITETOS" ? "bg-blue-500" :
                              gabinete.gabinete_type === "CONSTRUTORES" ? "bg-amber-500" :
                              "bg-purple-500"
                            }`}>
                              {gabinete.gabinete_type}
                            </span>
                          </td>
                          <td className="text-center">
                            <span className={`font-bold ${gabinete.total_quantity > 0 ? 'text-[#009392]' : 'text-stone-300'}`}>
                              {gabinete.total_quantity}
                            </span>
                          </td>
                          <td className="text-center text-stone-500">{gabinete.delivery_count}</td>
                          <td className="text-center text-stone-500">
                            {gabinete.last_delivery 
                              ? new Date(gabinete.last_delivery).toLocaleDateString('pt-PT')
                              : "-"
                            }
                          </td>
                        </tr>
                        {/* Expanded Deliveries Row */}
                        {isExpanded && gabineteDeliveries.length > 0 && (
                          <tr key={`${gabinete.gabinete_id}-deliveries`} className="bg-stone-50/50">
                            <td colSpan={7} className="p-0">
                              <div className="px-6 py-3 border-l-4 border-[#009392]">
                                <div className="space-y-2">
                                  {gabineteDeliveries.map((delivery) => (
                                    <div 
                                      key={delivery.id} 
                                      className="flex items-center justify-between bg-white rounded-lg p-3 border border-stone-200"
                                    >
                                      <div className="flex items-center gap-4">
                                        <span className="px-2 py-1 bg-[#009392]/10 text-[#009392] text-xs font-medium rounded">
                                          {delivery.catalog_name}
                                        </span>
                                        {editingGabineteDeliveryId === delivery.id ? (
                                          <>
                                            <div className="flex items-center gap-2">
                                              <span className="text-xs text-stone-500">Qtd:</span>
                                              <Input
                                                type="number"
                                                min="1"
                                                value={gabineteDeliveryForm.quantity}
                                                onChange={(e) => setGabineteDeliveryForm(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                                                className="w-16 h-8 text-sm text-center"
                                                onClick={(e) => e.stopPropagation()}
                                              />
                                            </div>
                                            <div className="flex items-center gap-2">
                                              <span className="text-xs text-stone-500">Data:</span>
                                              <Input
                                                type="date"
                                                value={gabineteDeliveryForm.delivery_date}
                                                onChange={(e) => setGabineteDeliveryForm(prev => ({ ...prev, delivery_date: e.target.value }))}
                                                className="w-32 h-8 text-sm"
                                                onClick={(e) => e.stopPropagation()}
                                              />
                                            </div>
                                          </>
                                        ) : (
                                          <>
                                            <span className="text-sm">
                                              <strong className="text-[#009392]">{delivery.quantity}</strong> unidades
                                            </span>
                                            <span className="text-sm text-stone-500">
                                              {new Date(delivery.delivery_date).toLocaleDateString('pt-PT')}
                                            </span>
                                          </>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-1">
                                        {editingGabineteDeliveryId === delivery.id ? (
                                          <>
                                            <button
                                              onClick={(e) => { e.stopPropagation(); handleSaveGabineteDelivery(); }}
                                              className="p-1.5 hover:bg-green-100 text-green-600 rounded transition-colors"
                                              data-testid={`btn-save-gabinete-delivery-${delivery.id}`}
                                            >
                                              <Check size={16} />
                                            </button>
                                            <button
                                              onClick={(e) => { e.stopPropagation(); setEditingGabineteDeliveryId(null); }}
                                              className="p-1.5 hover:bg-stone-100 text-stone-500 rounded transition-colors"
                                            >
                                              <X size={16} />
                                            </button>
                                          </>
                                        ) : (
                                          <>
                                            <button
                                              onClick={(e) => { e.stopPropagation(); handleEditGabineteDelivery(delivery); }}
                                              className="p-1.5 hover:bg-stone-100 text-stone-500 rounded transition-colors"
                                              data-testid={`btn-edit-gabinete-delivery-${delivery.id}`}
                                            >
                                              <PencilSimple size={16} />
                                            </button>
                                            <button
                                              onClick={(e) => { e.stopPropagation(); handleDeleteGabineteDelivery(delivery.id); }}
                                              className="p-1.5 hover:bg-red-100 text-stone-400 hover:text-red-500 rounded transition-colors"
                                              data-testid={`btn-delete-gabinete-delivery-${delivery.id}`}
                                            >
                                              <Trash size={16} />
                                            </button>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {/* Catalog Types Management */}
      <div className="gresart-card p-4">
        <h2 className="text-lg font-semibold font-[Manrope] text-stone-900 mb-4">
          Catálogos
        </h2>
        <div className="flex flex-wrap gap-2">
          {catalogs.map((catalog) => (
            <div 
              key={catalog.id}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-stone-100 border border-stone-200"
            >
              <BookOpen size={16} className="text-[#009392]" />
              <span className="text-sm font-medium text-stone-700">{catalog.name}</span>
              <span className="text-xs text-stone-400">({catalog.total_delivered})</span>
              <button
                onClick={() => handleDeleteCatalog(catalog.id)}
                className="ml-1 p-1 hover:bg-red-100 text-stone-400 hover:text-red-500 rounded transition-colors"
                data-testid={`btn-delete-catalog-${catalog.id}`}
              >
                <X size={14} />
              </button>
            </div>
          ))}
          {catalogs.length === 0 && (
            <p className="text-stone-500 text-sm">Nenhum tipo criado</p>
          )}
        </div>
      </div>

      {/* Add Catalog Type Dialog */}
      <Dialog open={showAddCatalog} onOpenChange={(open) => {
        setShowAddCatalog(open);
        if (!open) {
          setNewCatalogImage(null);
          setNewCatalogImagePreview(null);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Novo Tipo de PPM</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-medium text-stone-700 mb-1 block">
                Nome
              </Label>
              <Input
                placeholder="Ex: CATÁLOGO GERAL 2026"
                value={newCatalogName}
                onChange={(e) => setNewCatalogName(e.target.value)}
                data-testid="input-catalog-name"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-stone-700 mb-1 block">
                Ano
              </Label>
              <Input
                type="number"
                value={newCatalogYear}
                onChange={(e) => setNewCatalogYear(parseInt(e.target.value))}
                data-testid="input-catalog-year"
              />
            </div>
            
            {/* Catalog Image Upload - A4 Format */}
            <div>
              <Label className="text-sm font-medium text-stone-700 mb-1 block">
                Portada del Catálogo (formato A4)
              </Label>
              <input
                ref={catalogImageInputRef}
                type="file"
                accept="image/*"
                onChange={handleCatalogImageSelect}
                className="hidden"
              />
              
              {newCatalogImagePreview ? (
                <div className="relative mt-2 flex justify-center">
                  <div className="relative" style={{ aspectRatio: '1/1.4', height: '160px' }}>
                    <img 
                      src={newCatalogImagePreview} 
                      alt="Preview" 
                      className="h-full w-full object-cover rounded-lg border border-stone-200 shadow-sm"
                    />
                    <button
                      onClick={() => {
                        setNewCatalogImage(null);
                        setNewCatalogImagePreview(null);
                      }}
                      className="absolute -top-2 -right-2 p-1 bg-red-100 hover:bg-red-200 text-red-600 rounded-full"
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => catalogImageInputRef.current?.click()}
                  className="mt-2 w-full border-2 border-dashed border-stone-300 rounded-lg flex flex-col items-center justify-center gap-1 hover:border-[#00A19A] hover:bg-teal-50/50 transition-colors"
                  style={{ aspectRatio: '1/1.4', maxHeight: '160px' }}
                >
                  <CloudArrowUp size={28} className="text-stone-400" />
                  <span className="text-xs text-stone-500">Formato portada (A4)</span>
                </button>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowAddCatalog(false)}>
              Cancelar
            </Button>
            <Button 
              className="btn-primary" 
              onClick={handleAddCatalog} 
              disabled={uploadingCatalogImage}
              data-testid="btn-save-catalog"
            >
              {uploadingCatalogImage ? (
                <>
                  <LoadingSpinner size={16} className="mr-1" />
                  A guardar...
                </>
              ) : (
                <>
                  <Check size={18} />
                  Guardar
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Delivery Dialog */}
      <Dialog open={showAddDelivery} onOpenChange={(open) => {
        setShowAddDelivery(open);
        if (!open) {
          setEditingDeliveryId(null);
          setClientSearch("");
          setDeliveryForm({
            catalog_id: "",
            client_id: "",
            quantity: 1,
            delivery_date: new Date().toISOString().split('T')[0],
            notes: ""
          });
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingDeliveryId ? "Editar Entrega" : "Registar Entrega"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-stone-700 mb-1 block">
                PPM *
              </label>
              <Select 
                value={deliveryForm.catalog_id} 
                onValueChange={(v) => setDeliveryForm({...deliveryForm, catalog_id: v})}
              >
                <SelectTrigger data-testid="select-delivery-catalog">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {catalogs.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700 mb-1 block">
                Cliente *
              </label>
              <Input
                placeholder="Pesquisar cliente..."
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                className="mb-2"
                data-testid="input-search-client"
              />
              <Select 
                value={deliveryForm.client_id} 
                onValueChange={(v) => setDeliveryForm({...deliveryForm, client_id: v})}
              >
                <SelectTrigger data-testid="select-delivery-client">
                  <SelectValue placeholder="Selecione o cliente" />
                </SelectTrigger>
                <SelectContent className="max-h-[200px]">
                  {clients
                    .filter(c => 
                      !clientSearch || 
                      c.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
                      (c.city && c.city.toLowerCase().includes(clientSearch.toLowerCase()))
                    )
                    .map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name} {c.city ? `(${c.city})` : ''}
                      </SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1 block">
                  Quantidade
                </label>
                <Input
                  type="number"
                  min="1"
                  value={deliveryForm.quantity}
                  onChange={(e) => setDeliveryForm({...deliveryForm, quantity: parseInt(e.target.value) || 1})}
                  data-testid="input-delivery-quantity"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1 block">
                  Data de Entrega
                </label>
                <Input
                  type="date"
                  value={deliveryForm.delivery_date}
                  onChange={(e) => setDeliveryForm({...deliveryForm, delivery_date: e.target.value})}
                  data-testid="input-delivery-date"
                />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowAddDelivery(false)}>
              Cancelar
            </Button>
            <Button className="btn-primary" onClick={handleAddDelivery} data-testid="btn-save-delivery">
              <Check size={18} />
              {editingDeliveryId ? "Guardar" : "Registar"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Catalog Dialog */}
      <Dialog open={showEditCatalog} onOpenChange={(open) => {
        setShowEditCatalog(open);
        if (!open) {
          setEditingCatalog(null);
          setNewCatalogName("");
          setNewCatalogImage(null);
          setNewCatalogImagePreview(null);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Catálogo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-medium text-stone-700 mb-1 block">
                Nome
              </Label>
              <Input
                placeholder="Ex: CATÁLOGO GERAL 2026"
                value={newCatalogName}
                onChange={(e) => setNewCatalogName(e.target.value)}
                data-testid="input-edit-catalog-name"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-stone-700 mb-1 block">
                Ano
              </Label>
              <Input
                type="number"
                value={newCatalogYear}
                onChange={(e) => setNewCatalogYear(parseInt(e.target.value))}
                data-testid="input-edit-catalog-year"
              />
            </div>
            
            {/* Catalog Image Upload - A4 Format */}
            <div>
              <Label className="text-sm font-medium text-stone-700 mb-1 block">
                Portada / Foto do Catálogo (formato A4)
              </Label>
              <input
                ref={editCatalogImageInputRef}
                type="file"
                accept="image/*"
                onChange={handleEditCatalogImageSelect}
                className="hidden"
              />
              
              {newCatalogImagePreview ? (
                <div className="relative mt-2 flex justify-center">
                  <div className="relative" style={{ aspectRatio: '1/1.4', height: '180px' }}>
                    <img 
                      src={newCatalogImagePreview} 
                      alt="Preview" 
                      className="h-full w-full object-cover rounded-lg border border-stone-200 shadow-sm"
                    />
                    <button
                      onClick={() => editCatalogImageInputRef.current?.click()}
                      className="absolute bottom-2 left-1/2 transform -translate-x-1/2 px-3 py-1.5 bg-white/90 hover:bg-white text-xs text-stone-600 hover:text-[#009392] rounded-lg border border-stone-200 shadow-sm"
                    >
                      Cambiar portada
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => editCatalogImageInputRef.current?.click()}
                  className="mt-2 w-full border-2 border-dashed border-stone-300 rounded-lg flex flex-col items-center justify-center gap-1 hover:border-[#00A19A] hover:bg-teal-50/50 transition-colors"
                  style={{ aspectRatio: '1/1.4', maxHeight: '180px' }}
                >
                  <CloudArrowUp size={28} className="text-stone-400" />
                  <span className="text-xs text-stone-500">Seleccionar portada (A4)</span>
                </button>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowEditCatalog(false)}>
              Cancelar
            </Button>
            <Button 
              className="btn-primary" 
              onClick={handleSaveEditCatalog} 
              disabled={uploadingCatalogImage}
              data-testid="btn-save-edit-catalog"
            >
              {uploadingCatalogImage ? (
                <>
                  <LoadingSpinner size={16} className="mr-1" />
                  A guardar...
                </>
              ) : (
                <>
                  <Check size={18} />
                  Guardar
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
